import { useId, useState } from 'react';
import { DateTime } from 'luxon';
import TravellerCategorySelector from '../Shared/TravellerCategorySelector';
import { TravellerCategory } from '../../models/traveller-category.enum';
import Currency from '../Shared/Currency';
import type { IProduct } from '../../models/product.model';
import ProductViewModel from './models/product.view-model';
import { ProductType } from '../../models/product-type.enum';
import type { ITimeValidity } from '../../models/time-validity.model';
import { TimeValidityType } from '../../models/time-validity-type.enum';
import { TimeValidityUnit } from '../../models/time-validity-unit.enum';
import type { ITariffZone } from '../../models/tariff-zone.model';
import ShowMoreButton from '../Shared/ShowMoreButton';
import type { IServerSideRendered } from '../Shared/Base/server-side-rendered';
import { useTexts } from '../../hooks/texts.hook';

enum SortBy {
  type,
  validitytime,
  zone,
  price,
}

enum SortDirection {
  ascending,
  descending,
}

export interface ITicketPricesSettings {
  products: ProductViewModel[];
  defaultTravellerCategory: TravellerCategory;
}

export interface ITicketPricesTexts {
  travellerCategoryText: string;
  noDataText: string;
}

export type ITicketPricesProps = IServerSideRendered<ITicketPricesTexts, ITicketPricesSettings>;

function getAriaSortValue(column: SortBy, sortBy: SortBy, sortDirection: SortDirection): 'ascending' | 'descending' | 'none' | 'other' | undefined {
  if (column !== sortBy) {
    return undefined;
  }
  return sortDirection === SortDirection.ascending ? 'ascending' : 'descending';
}

function translateProductType(productType: ProductType, t: (sv: string, en: string) => string): string {
  switch (productType) {
    case ProductType.singleTicket:
      return t('Enkelbiljett', 'Single ticket');
    case ProductType.prepaidTicket:
      return t('Kontoladdning', 'Prepaid ticket');
    case ProductType.periodTicket:
      return t('Periodbiljett', 'Season ticket');
    case ProductType.dayTicket:
      return t('Dygnsbiljett', 'Day ticket');
    case ProductType.flexTicket:
      return t('Flexbiljett', 'Flex ticket');
    case ProductType.partialYearTicket:
      return t('Sommarbiljett', 'Summer ticket');
    case ProductType.leisureTicket:
      return t('Fritidsbiljett', 'Leisure ticket');
    default:
      return '';
  }
}

function translateTimeValidityUnit(unit: TimeValidityUnit, t: (sv: string, en: string) => string): string {
  switch (unit) {
    case TimeValidityUnit.minutes:
      return t('min', 'min');
    case TimeValidityUnit.hours:
      return t('h', 'h');
    case TimeValidityUnit.days:
      return t('dagar', 'days');
    case TimeValidityUnit.year:
      return t('år', 'year');
    case TimeValidityUnit.semester:
      return t('termin', 'semester');
    case TimeValidityUnit.schoolYear:
      return t('läsår', 'school year');
    case TimeValidityUnit.unlimited:
      return t('obegränsat', 'unlimited');
    default:
      return '';
  }
}

function convertTimeValidityToMinutes(timeValidity: ITimeValidity): number {
  switch (timeValidity.type) {
    case TimeValidityType.amountUnit:
      switch (timeValidity.unit) {
        case TimeValidityUnit.minutes:
          return timeValidity.amount!;
        case TimeValidityUnit.hours:
          return timeValidity.amount! * 60;
        case TimeValidityUnit.days:
          return timeValidity.amount! * 60 * 24;
        case TimeValidityUnit.semester:
          return timeValidity.amount! * 60 * 24 * 150; // Only for comparison
        case TimeValidityUnit.schoolYear:
          return timeValidity.amount! * 60 * 24 * 300; // Only for comparison
        case TimeValidityUnit.year:
          return timeValidity.amount! * 60 * 24 * 365;
        case TimeValidityUnit.unlimited:
          return Number.MAX_SAFE_INTEGER; // Only for comparison
        default:
          return 0;
      }
    case TimeValidityType.fromToDate:
      return timeValidity.toDate?.diff(timeValidity.fromDate!, 'minutes').minutes ?? 0;
    case TimeValidityType.fromToDateTime:
      return timeValidity.toDateTime?.diff(timeValidity.fromDateTime!, 'minutes').minutes ?? 0;
    default:
      return 0;
  }
}

function createZonesDisplayText(tariffZones: ITariffZone[]): string {
  const abc = tariffZones.filter((z) => z.number === 3000 || z.number === 3001 || z.number === 3002).map((z) => z.shortName);
  abc.sort();
  const notAbc = tariffZones
    .filter((z) => !(z.number === 3000 || z.number === 3001 || z.number === 3002))
    .map((z) => z.name)
    .map((zoneName) => zoneName.replace('  ', ' ')); // Fix for zones with double spaces, e.g. 'Varberg  B' which messes up the sorting
  notAbc.sort();
  return (abc.length > 0 ? [abc.join('')] : []).concat(notAbc).join(', ');
}

export default function TicketPrices({ settings, texts }: ITicketPricesProps) {
  // === STATE ===
  const [selectedTravellerCategory, setSelectedTravellerCategory] = useState(settings.defaultTravellerCategory);
  const [showAll, setShowAll] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.type);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ascending);

  // === VARIABLES ===
  const instanceId = useId();
  const { t } = useTexts();
  const products = settings.products.map(ProductViewModel.mapToBusinessModel);

  const showShowMoreButton = !showAll && products.filter((tp) => tp.travellerCategory === selectedTravellerCategory).length > 10;

  const sortedProducts: IProduct[] = [...products.filter((tp) => tp.travellerCategory === selectedTravellerCategory).slice(0, showAll ? Number.MAX_SAFE_INTEGER : 10)].sort((a, b) => {
    let result = 0;
    if (sortBy === SortBy.type) {
      result = translateProductType(a.productType, t).localeCompare(translateProductType(b.productType, t));
    }
    if (sortBy === SortBy.validitytime) {
      result = convertTimeValidityToMinutes(a.timeValidity) - convertTimeValidityToMinutes(b.timeValidity);
    }
    if (sortBy === SortBy.zone) {
      result = createZonesDisplayText(a.tariffZones).localeCompare(createZonesDisplayText(b.tariffZones));
    }
    if (sortBy === SortBy.price) {
      result = (a.priceInSek ?? 0) - (b.priceInSek ?? 0);
    }
    return result * (sortDirection === SortDirection.ascending ? 1 : -1);
  });

  // === HANDLERS ===
  function handleOnClick(column: SortBy): void {
    if (column === sortBy) {
      setSortDirection(sortDirection === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending);
    } else {
      setSortBy(column);
      setSortDirection(SortDirection.ascending);
    }
  }

  return (
    <div className="ticket-prices-block" aria-live="polite">
      <div className="mb-3">
        <TravellerCategorySelector id={instanceId} renderAsDropdown={false} selectedTravellerCategory={selectedTravellerCategory} onChange={setSelectedTravellerCategory}></TravellerCategorySelector>
      </div>
      <div className="small font-italic mb-3">{texts.travellerCategoryText}</div>
      <div className="table-responsive-sm">
        <table className="table table--sortable table--caption-visually-hidden mb-0">
          <caption>{t('Kolumnrubriker med knappar är sorterbara', 'Column headers with buttons are sortable')}</caption>
          <thead>
            <tr>
              <ColumnHeader column={SortBy.type} buttonText={t('Biljett', 'Ticket')} sortBy={sortBy} sortDirection={sortDirection} onClick={() => handleOnClick(SortBy.type)} />
              <ColumnHeader
                column={SortBy.validitytime}
                buttonText={t('Giltighetstid', 'Validity time')}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onClick={() => handleOnClick(SortBy.validitytime)}
              />
              <ColumnHeader column={SortBy.zone} buttonText={t('Zon', 'Zone')} sortBy={sortBy} sortDirection={sortDirection} onClick={() => handleOnClick(SortBy.zone)} />
              <ColumnHeader column={SortBy.price} buttonText={t('Pris', 'Price')} sortBy={sortBy} sortDirection={sortDirection} onClick={() => handleOnClick(SortBy.price)} />
            </tr>
          </thead>
          <tbody>
            {sortedProducts.map((tp) => (
              <tr key={tp.productId}>
                <td>{translateProductType(tp.productType, t)}</td>
                <td>
                  <TimeValidityValue value={tp.timeValidity} t={t} />
                </td>
                <td>{createZonesDisplayText(tp.tariffZones)}</td>
                <td>{tp.priceInSek && <Currency amount={tp.priceInSek} showFractionDigits={false} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortedProducts.length === 0 && <div className="mt-2 mx-2 font-italic">{texts.noDataText}</div>}
      <div>
        {showShowMoreButton && (
          <div className="mt-3">
            <ShowMoreButton ariaControls="blog-list-items" onClick={() => setShowAll(true)}></ShowMoreButton>
          </div>
        )}
      </div>
    </div>
  );
}

function ColumnHeader({ column, buttonText, sortBy, sortDirection, onClick }: { column: SortBy; buttonText: string; sortBy: SortBy; sortDirection: SortDirection; onClick: () => void }) {
  return (
    <th scope="col" aria-sort={getAriaSortValue(column, sortBy, sortDirection)}>
      <button type="button" className="btn--unstyled text-nowrap" onClick={onClick}>
        {buttonText}
        <span aria-hidden="true"></span>
      </button>
    </th>
  );
}

function TimeValidityValue({ value, t }: { value: ITimeValidity; t: (sv: string, en: string) => string }) {
  if (value.type === TimeValidityType.amountUnit) {
    return <span>{`${value.amount} ${translateTimeValidityUnit(value.unit, t)}`}</span>;
  }
  if (value.type === TimeValidityType.fromToDate) {
    return <span>{`${value.fromDate?.toLocaleString(DateTime.DATE_SHORT)} - ${value.toDate?.toLocaleString(DateTime.DATE_SHORT)}`}</span>;
  }
  if (value.type === TimeValidityType.fromToDateTime) {
    return <span>{`${value.fromDateTime?.toLocaleString(DateTime.DATETIME_SHORT)} - ${value.toDateTime?.toLocaleString(DateTime.DATETIME_SHORT)}`}</span>;
  }
  return <span>{JSON.stringify(value)}</span>;
}

